import {
  SparklesIcon,
  XMarkIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
} from "@heroicons/react/20/solid";
import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import axios from "axios";
import { useMatter } from "../../contexts/MatterContext";
import { Heading } from "../../design/heading";
import { Button } from "../../design/button";
import { Input } from "../../design/input";
import EmptyStateView from "../EmptyStateView";
import { getSubdomainFromUrl } from "../../utils/url";
import { IntakeMessage } from "../../models/IntakeMessage";

const API_URL = process.env.REACT_APP_API_URL + "/stream";

const ChatBubble: React.FC = () => {
  const { currentMatterId } = useMatter();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [messages, setMessages] = useState<IntakeMessage[]>([]);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);

  const toggleChat = () => setIsOpen(!isOpen);
  const toggleFullscreen = () => setIsFullscreen(!isFullscreen);

  const sendMessage = () => {
    if (!input.trim()) return;
    setIsSending(true);
    getCompletion(input);
  };

  const getCompletion = async (message: string) => {
    const userMessage: IntakeMessage = {
      _id: `${Date.now()}-user`,
      role: "user",
      content: message,
    };

    setMessages((prevMessages) => [...prevMessages, userMessage]);

    const aiMessageId = `${Date.now()}-ai`;

    // Temporarily add an empty AI response to the UI
    setMessages((prevMessages) => [
      ...prevMessages,
      { _id: aiMessageId, role: "ai", content: "" },
    ]);

    try {
      const payload = {
        message,
        conversationId: user?.uid,
        matterId: currentMatterId,
        tenantId: getSubdomainFromUrl(window.location.href),
      };

      const token = await user?.getIdToken();
      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok || !response.body) {
        throw new Error("Stream response failed");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let finalContent = "";
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });

        // Accumulate chunks to avoid word splitting
        buffer += chunk;

        // Only update when we have a complete sentence or phrase
        if (
          buffer.includes(".") ||
          buffer.includes("?") ||
          buffer.length > 50
        ) {
          finalContent += buffer;

          setMessages((prevMessages) =>
            prevMessages.map((msg) =>
              msg._id === aiMessageId ? { ...msg, content: finalContent } : msg,
            ),
          );

          buffer = ""; // Reset buffer
        }
      }

      // Ensure any remaining content is appended
      if (buffer.length > 0) {
        finalContent += buffer;
        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg._id === aiMessageId ? { ...msg, content: finalContent } : msg,
          ),
        );
      }
    } catch (error) {
      console.error("Error fetching stream:", error);
    } finally {
      setIsSending(false);
      setInput("");
    }
  };

  if (!currentMatterId) return null;

  return (
    <div className="fixed z-50 bottom-4 right-4">
      {/* Chat Bubble Icon */}
      {!isOpen && (
        <button
          onClick={toggleChat}
          className="p-4 text-white transition-all rounded-full shadow-lg dark:text-black bg-zinc-900 dark:bg-white hover:bg-gray-700 dark:hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <SparklesIcon className="w-6 h-6" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className={`flex flex-col bg-white dark:bg-zinc-800 rounded-lg shadow-lg ${
            isFullscreen ? "fixed inset-0 z-50" : "w-80 h-96"
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 rounded-t-lg">
            <Heading>Assistant</Heading>
            <div className="flex items-center space-x-2">
              <button
                onClick={toggleFullscreen}
                className="hover:text-gray-300"
              >
                {isFullscreen ? (
                  <ArrowsPointingInIcon className="w-6 h-6" />
                ) : (
                  <ArrowsPointingOutIcon className="w-6 h-6" />
                )}
              </button>
              <button onClick={toggleChat} className="hover:text-gray-300">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 p-4 space-y-2 overflow-y-auto">
            {messages.length === 0 && (
              <EmptyStateView
                title="No messages yet"
                message="Please ask a question below to get started"
              />
            )}
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${
                  message.role === "user" ? "justify-end" : "justify-start"
                }`}
                style={{ whiteSpace: "pre-line" }}
              >
                <div
                  className={`${
                    message.role === "user"
                      ? "bg-blue-500 text-white"
                      : "bg-gray-200 text-black"
                  } p-2 rounded-lg max-w-xs`}
                >
                  {message.content}
                </div>
              </div>
            ))}
          </div>

          {/* Input */}
          <div className="flex items-center p-2 border-t">
            <Input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message..."
            />
            <Button
              onClick={sendMessage}
              className="px-4 py-2 ml-2 text-white bg-blue-500 rounded-lg hover:bg-blue-600"
            >
              {isSending ? "..." : "Send"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatBubble;
